
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendMessageNotification } from "@/services/notifications";
import { Message } from "./types";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('sendMessage');

// Send a message to another user
export const sendMessage = async (
  recipientId: string, 
  content: string, 
  reagentId?: string
): Promise<boolean> => {
  try {
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      toast.error("You must be logged in to send messages");
      return false;
    }

    const senderId = sessionData.session.user.id;
    
    logger.debug(`Sending message from ${senderId} to ${recipientId}${reagentId ? ` about reagent ${reagentId}` : ''}`);
    
    // Insert the new message
    const { data, error } = await supabase
      .from("messages")
      .insert({
        sender_id: senderId,
        recipient_id: recipientId,
        content,
        reagent_id: reagentId,
        read: false
      })
      .select('*, payload')
      .single();

    if (error) {
      logger.error("Error sending message:", error);
      return false;
    }

    logger.debug("Message sent successfully");

    // Get sender info for notification in a separate try/catch to prevent failures
    try {
      const { data: senderData } = await supabase
        .from("profiles")
        .select("display_username, username")
        .eq("id", senderId)
        .single();
      
      const senderName = senderData?.display_username || senderData?.username || "Another user";
      
      // Get reagent name if reagentId is provided
      let reagentName;
      if (reagentId) {
        const { data: reagentData } = await supabase
          .from("reagents")
          .select("name")
          .eq("id", reagentId)
          .single();
        
        if (reagentData) {
          reagentName = reagentData.name;
        }
      }

      // Send notification email to recipient
      logger.debug("Attempting to send message notification...");
      await sendMessageNotification(
        recipientId, 
        senderId, 
        content, 
        senderName,
        reagentName
      );
    } catch (notificationError) {
      logger.error("Error with notification (non-critical):", notificationError);
      // Don't fail the send operation for notification issues
    }

    return true;
  } catch (error) {
    logger.error("Exception in sendMessage:", error);
    return false;
  }
};
